import {
  Accordion,
  AccordionSummary,
  Container,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
// components
import { yupResolver } from "@hookform/resolvers/yup";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import moment from "moment";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import useApiServices from "src/api/use-api-services";
import AppButton from "src/components/common/app-button";
import FormProvider from "src/components/hook-form";
import Iconify from "src/components/iconify";
import Scrollbar from "src/components/scrollbar/Scrollbar";
import { NAV } from "src/config-global";
import { filtersDefaultValues } from "src/constants/filters-default-values";
import { PLATFORMS } from "src/constants/platforms";
import { ReturnType } from "src/hooks/use-boolean";
import { ICustomFiltersDto } from "src/types/filters/custom-filters";
import { FormType } from "src/types/filters/filters";
import { useQueryParams } from "use-query-params";
import { DashboardFilterSchema } from "../../main/filters/schema";
import EndDateFilter from "./sections/date-filter/end-date-filter";
import StartDateFilter from "./sections/date-filter/start-date-filter";
import MultiCheckboxFilter from "./sections/multi-select-filter";
// ----------------------------------------------------------------------

type Props = {
  platform: keyof typeof PLATFORMS;
  type: "main" | "custom";
  open: ReturnType;
  isActivityCompleted: boolean;
};

export default function FilterDrawer({ platform, type, open, isActivityCompleted }: Props) {
  // #region Hooks and states
  const { useGetListApi } = useApiServices();

  const [query, setQuery] = useQueryParams<FormType>(filtersDefaultValues);

  const filtersForm = useForm({
    resolver: yupResolver(DashboardFilterSchema),
    defaultValues: {
      start_date: query.start_date || "",
      end_date: query.end_date || "",

      keyword: query.keyword || [],
      language: query.language || [],
      sentiment: query.sentiment || [],
      author_verified: query.author_verified || [],
    },
  });
  // #endregion

  // #region Services
  const { data: filtersData } = useGetListApi<ICustomFiltersDto>({
    url: PLATFORMS[platform][type].FILTERS,
    onSuccess: (data: ICustomFiltersDto) => {
      // data.filters.start_date && filtersForm.setValue("start_date", data.filters.start_date!);
      // data.filters.end_date && filtersForm.setValue("end_date", data.filters.end_date!);
    },
    parameters: query,
  });
  // #endregion

  // #region Handlers
  const onSubmit = useCallback(async () => {
    setQuery({
      ...(filtersForm.watch() as FormType),
      end_date: filtersForm.watch().end_date
        ? moment(filtersForm.watch().end_date).format("yyyy-MM-DD")
        : undefined,
      start_date: filtersForm.watch().start_date
        ? moment(filtersForm.watch().start_date).format("yyyy-MM-DD")
        : undefined,
    });
  }, [filtersForm, setQuery]);

  const handleReset = () => {
    filtersForm.reset();
    setQuery({
      ...filtersDefaultValues,

      // Keyword should always be sent in custom dashboard
      keyword: query.keyword,

      // Dates should be always sent
      end_date: filtersData?.filters.end_date,
      start_date: filtersData?.filters.start_date,
    });
  };
  // #endregion

  return (
    <>
      <AppButton
        disableRipple
        startIcon={<Iconify icon="ic:round-filter-list" />}
        onClick={open.onTrue}
        label="Filters"
        variant="outlined"
        size="large"
        fullWidth={false}
        disabled={!isActivityCompleted}
      />

      <Drawer
        anchor="right"
        open={open.value}
        onClose={open.onFalse}
        PaperProps={{
          sx: { width: NAV.W_DASHBOARD },
        }}
      >
        <FormProvider methods={filtersForm} onSubmit={filtersForm.handleSubmit(onSubmit)}>
          <Stack justifyContent="space-between">
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ pl: 2, pr: 1, py: 2 }}
            >
              <Typography variant="subtitle1">Filters</Typography>
              <IconButton onClick={open.onFalse}>
                <Iconify icon="eva:close-fill" />
              </IconButton>
            </Stack>

            <Divider />

            <Scrollbar sx={{ height: 500 }}>
              <Stack spacing={3} sx={{ p: 2.5 }}>
                <FilterContainer label="Date filter">
                  <Stack spacing={1.5} pb={3}>
                    <StartDateFilter dashboardFilter={filtersData?.filters!} />
                    <EndDateFilter dashboardFilter={filtersData?.filters!} />
                  </Stack>
                </FilterContainer>
                {filtersData?.filters.topic?.length && (
                  <FilterContainer label="Topic filter">
                    <MultiCheckboxFilter
                      options={filtersData?.filters?.topic || []}
                      name="topicFilter"
                    />
                  </FilterContainer>
                )}
                {!!filtersData?.filters.language?.length && (
                  <FilterContainer label="Language filter">
                    <MultiCheckboxFilter
                      options={filtersData?.filters?.language || []}
                      name="language"
                    />
                  </FilterContainer>
                )}
                {!!filtersData?.filters.keyword?.length && (
                  <FilterContainer label="Keyword filter">
                    <MultiCheckboxFilter
                      options={filtersData?.filters.keyword || []}
                      name="keyword"
                    />
                  </FilterContainer>
                )}
                {!!filtersData?.filters.sentiment?.length && (
                  <FilterContainer label="Sentiment filter">
                    <MultiCheckboxFilter
                      options={filtersData?.filters.sentiment || []}
                      name="sentiment"
                    />
                  </FilterContainer>
                )}
                {!!filtersData?.filters.author_verified?.length && (
                  <FilterContainer label="Author verification">
                    <MultiCheckboxFilter
                      options={(filtersData?.filters.author_verified || []).map(String)}
                      name="author_verified"
                    />
                  </FilterContainer>
                )}
              </Stack>
            </Scrollbar>

            <Stack
              spacing={1}
              sx={{ px: 2.5, py: 1, position: "sticky", bottom: 0, bgcolor: "background.paper" }}
            >
              <AppButton
                label="Apply Filters"
                startIcon={<Iconify icon="eva:checkmark-circle-outline" />}
                size="large"
                type="submit"
                variant="outlined"
              />
              <AppButton
                onClick={handleReset}
                label="Reset Filters"
                startIcon={<Iconify icon="eva:trash-2-outline" />}
                size="large"
                variant="outlined"
              />
            </Stack>
          </Stack>
        </FormProvider>
      </Drawer>
    </>
  );
}

const FilterContainer = ({ children, label }: { children: JSX.Element; label: string }) => (
  <Accordion
    sx={{
      borderRadius: 2,
    }}
  >
    <AccordionSummary
      expandIcon={<ExpandMoreIcon />}
      aria-controls="panel2a-content"
      id="panel2a-header"
    >
      <Typography variant="caption">{label}</Typography>
    </AccordionSummary>
    <Container>{children}</Container>
  </Accordion>
);
